import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, X, Check } from "lucide-react";
import { toast } from "sonner";
import { api, extractError } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PosterCaptureDialog({ open, onClose, onDone }) {
  const videoRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState("");

  const capture = () => {
    const v = videoRef.current;
    if (!v) return null;
    const cvs = document.createElement("canvas");
    cvs.width = v.videoWidth || 640;
    cvs.height = v.videoHeight || 360;
    cvs.getContext("2d").drawImage(v, 0, 0, cvs.width, cvs.height);
    return cvs;
  };

  const previewFrame = () => {
    const cvs = capture();
    if (!cvs) return;
    setPreview(cvs.toDataURL("image/jpeg", 0.85));
  };

  const useFrame = async () => {
    const cvs = capture();
    if (!cvs) return;
    setBusy(true);
    try {
      const blob = await new Promise((res) => cvs.toBlob(res, "image/jpeg", 0.9));
      if (!blob) throw new Error("لم يتمكّن المتصفح من إنشاء الصورة");
      const fd = new FormData();
      fd.append("file", new File([blob], "poster.jpg", { type: "image/jpeg" }));
      await api.post("/admin/director-video-poster-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("تم تحديث الغلاف من هذا الإطار");
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(extractError(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="poster-capture">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black inline-flex items-center gap-2">
            <Camera className="w-6 h-6 text-emerald-600" /> استخراج غلاف من الفيديو
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-500">شغّل الفيديو، أوقفه عند اللحظة المطلوبة، ثم اضغط "استخدم هذا الإطار".</p>
          <video
            ref={videoRef}
            src={`${API}/public/director-video`}
            controls
            crossOrigin="anonymous"
            className="w-full aspect-video bg-black rounded-xl"
            data-testid="poster-capture-video"
          />
          {preview && (
            <div className="rounded-xl overflow-hidden border-2 border-emerald-200">
              <img src={preview} alt="preview" className="w-full max-h-64 object-contain bg-slate-50" data-testid="poster-capture-preview" />
              <div className="p-2 bg-emerald-50 text-xs font-black text-emerald-700">معاينة الغلاف</div>
            </div>
          )}
          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="outline" onClick={onClose} disabled={busy} data-testid="poster-capture-cancel">
              <X className="w-4 h-4" /> إلغاء
            </Button>
            <Button variant="outline" onClick={previewFrame} disabled={busy} className="gap-1" data-testid="poster-capture-preview-btn">
              <Camera className="w-4 h-4" /> معاينة
            </Button>
            <Button onClick={useFrame} disabled={busy} className="gap-2 bg-gradient-to-l from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700" data-testid="poster-capture-use">
              <Check className="w-4 h-4" /> {busy ? "جارٍ الرفع..." : "استخدم هذا الإطار"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
